import Konva from 'konva'
import _ from 'lodash'
import { ShapeGetClientRectConfig } from 'konva/lib/Shape'
import { Vector2d, IRect } from 'konva/lib/types'
import { OnScale } from 'vue-moveable'
import { MoveableDirections } from './enums'
import {
  SegmentCollection,
  SegmentText,
  SegmentVideo,
} from '@ve-pro/types/track.type'
import { useProjectStore, useTrackStore } from '@ve-pro/store'
import { getTrackIndex } from '../utils'
import { isSupportPosition } from '@ve-pro/store/tracks/segment/segment-position'
import { isSegmentEliminationText } from '@ve-pro/store/tracks/segment/segment-text'
import { isSupportRatios } from '@ve-pro/store/tracks/segment/segment-ratio'
import { isLangSegment } from '@ve-pro/store/tracks/tracks-actions'
import { isSegmentSubtitle } from '@ve-pro/store/tracks/segment/segment-subtitle'
import { computedPercent } from '@ve-pro/replace/components/text-setting-popover/utils'
import {
  getGroupBySegment,
  isSegmentGroup,
} from '@ve-pro/store/tracks/segment/segment-group'
import { update } from '@ve-pro/replace/multi-languages/uploaded-assets/utils'

interface Point {
  x1: number
  y1: number
  x2: number
  y2: number
}

/**
 * 通过nodes获取整个大Rect
 */
export function getRectFromNodes(
  nodes: Konva.Rect[],
  config?: ShapeGetClientRectConfig,
): IRect {
  if (!nodes.length) {
    return {
      x: 0,
      y: 0,
      width: 0,
      height: 0,
    }
  }

  const points: Point[] = []

  nodes.forEach((n) => {
    const rect = n.getClientRect(config)

    points.push({
      x1: rect.x,
      y1: rect.y,
      x2: rect.x + rect.width,
      y2: rect.y + rect.height,
    })
  })

  const point = {
    x1: points.reduce((acc, cur) => Math.min(acc, cur.x1), Infinity),
    y1: points.reduce((acc, cur) => Math.min(acc, cur.y1), Infinity),
    x2: points.reduce((acc, cur) => Math.max(acc, cur.x2), -Infinity),
    y2: points.reduce((acc, cur) => Math.max(acc, cur.y2), -Infinity),
  }

  return {
    x: point.x1,
    y: point.y1,
    width: point.x2 - point.x1,
    height: point.y2 - point.y1,
  }
}

/**
 * 获取点击中的node 基于obb
 */
export function getNodeByPoint(
  point: Vector2d,
  nodes: Konva.Rect[],
): Konva.Rect | undefined {
  return nodes
    .sort((a, b) => b.parent!.getAttr('_zIndex') - a.parent!.getAttr('_zIndex'))
    .find((node) => node.intersects(point))
}

/**
 * 判断两个元件是否重叠
 * @param node1 框选元件，矩形，不能包含旋转角度
 * @param node2 被检测元件
 * @returns true为重叠，false不重叠
 */
export function checkRectanglesOverlap(
  node1: Konva.Rect,
  node2: Konva.Rect,
): boolean {
  const stage = node1.getStage()
  if (!stage) {
    return false
  }

  const bufferHitCanvas = stage.bufferHitCanvas

  bufferHitCanvas.getContext().clear()
  node2.drawHit(bufferHitCanvas, undefined, true)

  const rect = node1.getClientRect()

  return (
    new Uint8Array(
      bufferHitCanvas.context.getImageData(
        rect.x,
        rect.y,
        rect.width,
        rect.height,
      ).data.buffer,
    ).reduce((a, b) => a + b) > 0
  )
}

/**
 * 获取缩放后的rect
 * @param rect
 * @param scale
 * @returns
 */
export function transformRectToScale(
  rect: IRect,
  scale: number,
  rotation = 0,
): IRect {
  const r = new Konva.Rect({
    x:
      rotation === Math.PI
        ? rect.x - rect.width + rect.width / 2
        : rect.x + rect.width / 2,
    y:
      rotation === Math.PI
        ? rect.y - rect.height + rect.height / 2
        : rect.y + rect.height / 2,
    width: rect.width,
    height: rect.height,
    offsetX: rect.width / 2,
    offsetY: rect.height / 2,
    // rotation: rotation * (180 / Math.PI),
  })
  r.scale({
    x: scale,
    y: scale,
  })

  const clientRect = r.getClientRect()

  return {
    ...clientRect,
    x: rotation === Math.PI ? clientRect.x + clientRect.width : clientRect.x,
    y: rotation === Math.PI ? clientRect.y + clientRect.height : clientRect.y,
  }
}

export const segmentClassName = 'segment-transformer'

export function isWidthDirection(direction: OnScale['direction']): boolean {
  return [MoveableDirections.E, MoveableDirections.W].some(
    (d) => d[0] === direction[0] && d[1] === direction[1],
  )
}
export function isHeightDirection(direction: OnScale['direction']): boolean {
  return [MoveableDirections.N, MoveableDirections.S].some(
    (d) => d[0] === direction[0] && d[1] === direction[1],
  )
}

export const DATA_SEGMENT_ID = 'data-segment-id'

export function querySegment(
  root: HTMLElement | Document,
  id: SegmentCollection['id'],
): HTMLDivElement | null {
  return root.querySelector<HTMLDivElement>(
    `.${segmentClassName}[${DATA_SEGMENT_ID}="${id}"]`,
  )
}

export function getSegmentByElement(
  element: HTMLElement,
): SegmentCollection | undefined {
  const trackStore = useTrackStore()

  const id = Number(element.getAttribute(DATA_SEGMENT_ID))
  return trackStore.allSegments.find((s) => s.id === id)
}

export function getSegmentByPoint(
  root: HTMLElement,
  segments: SegmentCollection[],
  point: { x: number; y: number },
): SegmentCollection | undefined {
  const { x, y } = point

  return segments
    .sort((a, b) => getTrackIndex(b.track_id) - getTrackIndex(a.track_id))
    .find((segment) => {
      const dom = querySegment(root, segment.id)
      if (!dom) return false
      const rect = dom.getBoundingClientRect()

      return (
        x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom
      )
    })
}

/**
 * 同步视频元件的位置到所属文本替换、文本消除、字幕上
 */
export function syncVideoPositionWithText(
  texts: SegmentText[],
  offset: { x: number; y: number },
): void {
  texts.forEach((segmentText) => {
    if (!isSupportPosition(segmentText)) return

    segmentText.x += offset.x
    segmentText.y += offset.y
  })
}

export function updateSegmentEliminationLayoutToRatios(): void {
  const trackStore = useTrackStore()
  if (!trackStore.activeSegment) return

  if (
    isSegmentEliminationText(trackStore.activeSegment) &&
    isSupportRatios(trackStore.activeSegment)
  ) {
    Object.values(trackStore.activeSegment.ratios ?? {}).forEach((ratio) => {
      // @ts-ignore
      ratio.width = trackStore.activeSegment.width
      // @ts-ignore
      ratio.height = trackStore.activeSegment.height
    })
  }
}

/**
 * 同步视频的字幕、ai消除文本的位置大小到其它字幕上
 * layout 处理宽高
 */
export function syncVideoTextProps(segment: SegmentText, layout = false): void {
  const projectStore = useProjectStore()
  const trackStore = useTrackStore()

  if (!projectStore.isReplaceProject) return

  segment = isLangSegment(segment)
    ? (trackStore.segments.find((s) => {
        return s.id === segment.source_segment_id && s.type === segment.type
      }) as SegmentText) ?? segment
    : segment

  const isSubtitle = isSegmentSubtitle(segment)
  const isElimination = isSegmentEliminationText(segment)

  let texts: SegmentText[] = []
  // 如果是字幕，同步所以视频的字幕位置
  if (isSubtitle) {
    texts = trackStore.segments
      .filter(isSegmentSubtitle)
      .filter((s) => !isLangSegment(s))
      // 按主视频scope
      .filter((s) => s.source_segment_id === segment.source_segment_id)
  } else if (isElimination) {
    // 如果是ai消除的文本，只同步自已
    texts = [segment]
  }

  const video = trackStore.allSegments.find(
    (s) => s.id === segment.source_segment_id,
  )

  if (video && isSupportRatios(video)) {
    const percent = computedPercent(segment, video)
    const widthPercent = segment.width / video.width

    const group = _.groupBy(texts, 'source_segment_id')

    _.forEach(group, (texts, videoId) => {
      const relativeVideo = trackStore.allSegments.find(
        (s) => s.id === Number(videoId),
      ) as SegmentVideo
      const videoGroup = getGroupBySegment(relativeVideo) ?? relativeVideo
      if (!relativeVideo) return
      texts.forEach((text) => {
        if (layout) {
          if (!text.ratios) return
          Object.keys(text.ratios).forEach((ratio) => {
            // @ts-ignore
            text.ratios[ratio].width = Math.round(
              // @ts-ignore
              videoGroup.ratios[ratio].width * widthPercent,
            )
          })
        } else {
          // @ts-ignore
          update(text, videoGroup, percent)

          // group
          if (isSegmentGroup(text)) {
            trackStore.segmentGroups[text.id]?.forEach((child) => {
              // @ts-ignore
              update(child, videoGroup, percent)
            })
          }
        }
      })
    })
  }
}

// 快速替换视频的字幕、ai消除文本的位置、大小
export function updateVideoTextsBounds(layout = false) {
  const projectStore = useProjectStore()
  const trackStore = useTrackStore()

  if (!projectStore.isReplaceProject) return

  const texts = trackStore.segments
    .filter((s) => trackStore.activeSegments.includes(s.id))
    .filter(
      (s) => isSegmentSubtitle(s) || isSegmentEliminationText(s),
    ) as SegmentText[]

  texts.forEach((text) => {
    syncVideoTextProps(text, layout)
  })
}
